import * as Yup from 'yup';
import { Grid } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useDataRoomDocumentFoldersQuery } from '../../shared/hooks';
import { DataRoomFolder } from '../../shared/models/DataRoomFolder';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface EditFolderFormProps {
	dataRoomGuid: string | undefined;
	folder: DataRoomFolder | null | undefined;
	close: (folder: DataRoomFolder | null | undefined) => void;
}

export const EditFolderForm: React.FC<EditFolderFormProps> = ({ dataRoomGuid, folder, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const foldersQuery = useDataRoomDocumentFoldersQuery(dataRoomGuid);

	const addFolderMutation = useMutation(
		async ({ values }: EditFolderFormMutationVariables) => {
			const folderPatch = {
				name: values.name,
				folderGuid: values.folderGuid,
				isVisibleToAll: values.isVisibleToAll,
				dataRoomUsers: values.dataRoomUsers,
			};

			const { data, error } = await getApiResponseData<DataRoomFolder>(
				api.patch(CerebroApiEndpoints.Documents.Folder(dataRoomGuid, folder?.guid), folderPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				folder = data;
				client.invalidateQueries(QueryKeys.DataRoom.Folders(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, folder?.guid));
				snackbar.enqueueSnackbar('Data room folder was edited.', { variant: 'success' });
				window.location.reload();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer
			open={folder != null}
			onClose={() => {
				close(null);
				window.location.reload();
			}}
			PaperProps={{ sx: { width: '500px' } }}
		>
			<CerebroHeading>{folder?.name}</CerebroHeading>
			<Formik
				validationSchema={EditFolderSchema}
				initialValues={{
					name: folder?.name ?? '',
					folderGuid: folder?.folderGuid,
					isVisibleToAll: folder?.isVisibleToAll ?? true,
					dataRoomUsers: folder?.dataRoomUsers as number[],
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addFolderMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="name">Folder Name</CerebroLabel>
									<Field
										id="name"
										as={CerebroInput}
										name="name"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('name', errors, touched)}
									/>
									{FormikUtils.IsInvalid('name', errors, touched) && (
										<CerebroText variant="error">{errors.name}</CerebroText>
									)}
								</Grid>

								{folder?.isParentVisibleToAll && (
									<Grid item xs={8}>
										<CerebroLabel id="isVisibleToAll">All Access</CerebroLabel>
										<Field
											id="isVisibleToAll"
											as={CerebroYesNo}
											name="isVisibleToAll"
											disabled={isSubmitting}
										/>
									</Grid>
								)}

								{!values.isVisibleToAll && (
									<>
										<Grid item xs={8}>
											<CerebroLabel required id="reasons">
												Data Room Users
											</CerebroLabel>
											<Field
												id="dataRoomUsers"
												as={CerebroMultiDropdown}
												name="dataRoomUsers"
												disabled={isSubmitting}
												error={FormikUtils.IsInvalid('dataRoomUsers', errors, touched)}
											>
												{folder?.activeDataRoomUsers.map(user => (
													<CerebroMultiDropdownMenuItem key={user.guid} value={user.id}>
														{user.fullName} ({user.bankName})
													</CerebroMultiDropdownMenuItem>
												))}
											</Field>
											{FormikUtils.IsInvalid('dataRoomUsers', errors, touched) && (
												<CerebroText variant="error">{errors.dataRoomUsers}</CerebroText>
											)}
										</Grid>
									</>
								)}

								{foldersQuery.isSuccess && (
									<Grid item xs={8}>
										<CerebroLabel>Parent Folder</CerebroLabel>
										<Field
											id="folderGuid"
											as={CerebroDropdown}
											name="folderGuid"
											disabled={isSubmitting}
										>
											{foldersQuery.data
												.filter(
													item =>
														item.guid != folder?.guid &&
														(item.guid == folder?.folderGuid || item.isVisibleToAll)
												)
												.map(folder => (
													<CerebroMultiDropdownMenuItem key={folder.guid} value={folder.guid}>
														{folder.name == 'Data Room Folder'
															? 'Root Folder'
															: folder.name}
													</CerebroMultiDropdownMenuItem>
												))}
										</Field>
										<CerebroText variant="hint">
											Folders can not be moved into private folders.
										</CerebroText>
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type EditFolderFormMutationVariables = FormikUtils.MutateOnSubmitVariables<EditFolderFormValues>;
interface EditFolderFormValues {
	name: string;
	folderGuid: string | null | undefined;
	isVisibleToAll: boolean;
	dataRoomUsers: number[];
}

const EditFolderSchema = Yup.object().shape({
	name: Yup.string(),
});
